import React, { FC, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { useMount } from "react-use";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";

import { StandardLayout } from "@/components/layouts";
import { ProgressBar } from "@/components/molecules";
import { Communication, FormPanel, IdentityCard } from "@/components/organisms";
import { requestApi } from "@/shared/apis";
import { AddFamilyMemberPageState } from "@/shared/domain";
import { useAuth, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import { DriversLicense, Finance, Person, PersonalInfo } from "../components";
import { formSchema } from "../formSchema";
import { defaultValues, FormType, mapRequestFamilyMember } from "../utils";
import { StyledAddMember, StyledSubtitle } from "./styled";

enum Steps {
    PersonStep = 0,
    PersonalInfoStep = 1,
    AdditionalInfoStep = 2,
}

const AddFamilyMemberPage: FC = () => {
    const {
        state: { policyholderId, policyholderName },
    } = useRouterHook<AddFamilyMemberPageState>();
    const [step, setStep] = useState(Steps.PersonStep);
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { navToPolicyholderDetail, navToRoot } = useNavigation();

    if (!policyholderId) navToRoot();

    const { mutate: requestFamilyMember, isLoading } = useMutation(requestApi.requestFamilyMember, {
        onSuccess: async () => {
            queryClient.invalidateQueries("requestsPaginated");
            queryClient.invalidateQueries(["relations", { policyholderId }]);
        },
    });

    const { incUsageCount } = useUserFeedback();

    const methods = useForm<FormType>({
        mode: "onBlur",
        defaultValues,
        resolver: yupResolver(formSchema),
    });

    useMount(() => {
        incUsageCount();
        tagManager.pushAddFamilyMemberBeginEvent();
    });

    const onSubmit = (formValues: FormType) => {
        const familyMember = mapRequestFamilyMember(formValues, policyholderId);
        try {
            requestFamilyMember({
                policyholderId,
                body: familyMember,
                params: {
                    ibpAclId: user.getIbpAclId(policyholderId),
                    relationTypeId: formValues.relationType,
                    requestId: formValues.id || -1,
                },
            });
            tagManager.pushAddFamilyMemberSentEvent();
            navToPolicyholderDetail(policyholderId);
        } catch (e) {
            tagManager.pushAddFamilyMemberSentEvent(false);
            throw e;
        }
    };

    const ref = "";

    return (
        <StandardLayout title={t("PageTitles.AddFamilyMember")} disableSearch>
            <div className="header">
                <span className="title">
                    {t("AddFamilyMember.General.Title", { ref, memberName: policyholderName })}
                </span>
                <StyledSubtitle>{t("AddFamilyMember.General.SubTitle")}</StyledSubtitle>
            </div>
            <ProgressBar
                currentStep={step}
                steps={Object.keys(Steps).length / 2}
                startLabel={t("AddFamilyMember.General.GeneralInfo")}
                endLabel={t("AddFamilyMember.General.ComplementaryInfo")}
            />
            <StyledAddMember>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className="addMemberRow">
                            <div className="addMemberCol">
                                <FormPanel title={t("AddFamilyMember.General.WhoWouldYouLikeToAdd")} mode="edit">
                                    <Person onComplete={() => setStep(Steps.PersonalInfoStep)} />
                                </FormPanel>
                                {step >= Steps.PersonalInfoStep && (
                                    <FormPanel
                                        title={t("AddFamilyMember.General.WhatIsHisPersonalInformation?")}
                                        mode="edit"
                                    >
                                        <PersonalInfo onComplete={() => setStep(Steps.AdditionalInfoStep)} />
                                    </FormPanel>
                                )}
                            </div>
                            <div className="addMemberCol">
                                {step >= Steps.AdditionalInfoStep && (
                                    <>
                                        <FormPanel
                                            title={t("AddFamilyMember.General.WhatIsHisIdentityData?")}
                                            mode="edit"
                                        >
                                            <IdentityCard />
                                        </FormPanel>
                                        <FormPanel
                                            title={t("AddFamilyMember.General.WhatAreItsFinancialData?")}
                                            mode="edit"
                                        >
                                            <Finance />
                                        </FormPanel>
                                        <FormPanel
                                            title={t("AddFamilyMember.General.HowToCommunicateWithThisPerson?")}
                                            mode="edit"
                                        >
                                            <Communication />
                                        </FormPanel>
                                        <FormPanel title={t("AddFamilyMember.General.HasADrivingLicense?")} mode="edit">
                                            <DriversLicense />
                                        </FormPanel>
                                        <Button
                                            type="submit"
                                            disabled={isLoading}
                                            className="float-right mb10 mt10"
                                            data-testid="button_confirm"
                                        >
                                            {t("Common.Confirm")}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </StyledAddMember>
        </StandardLayout>
    );
};

export default AddFamilyMemberPage;
